/**
 * Auth utilities for Coach Cedar
 */

import { storage } from "../services/storage";
import { Platform } from "react-native";
import { API_BASE_URL } from "./api"; // Centralized environment-aware API URL

// Get JWT token from various sources (platform-safe)
export const getAuthToken = async (): Promise<string | null> => {
  // Cookies only exist on web
  const getCookie = (name: string) => {
    if (typeof document === "undefined") return null;
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(";").shift() || null;
    return null;
  };

  if (Platform.OS === "web") {
    const cookieToken = getCookie("jwt");
    if (cookieToken) return cookieToken;
  }

  // Shared storage (AsyncStorage on native, localStorage on web)
  const storedToken = await storage.getItem("jwt_token");
  if (storedToken) return storedToken;

  return null;
};

// Get auth headers for API requests (platform-safe)
export const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const token = await getAuthToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // Add user ID if available
  const savedUser = await storage.getItem("cedar_user");
  if (savedUser) {
    try {
      const user = JSON.parse(savedUser);
      if (user.id) {
        headers["X-User-ID"] = user.id;
      }
    } catch (e) {
      console.error("Failed to parse user data");
    }
  }

  return headers;
};

// Development: Create a dev auth token (web-only)
export const createDevAuthToken = async (userId?: string): Promise<string | null> => {
  if (Platform.OS !== "web") return null;
  try {
    const response = await fetch(`${API_BASE_URL}/api/dev-auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        user_id: userId,
      }),
    });

    if (!response.ok) {
      console.error("Failed to create dev auth token");
      return null;
    }

    const data = await response.json();

    // Store token in shared storage for future use
    if (data.token) {
      await storage.setItem("jwt_token", data.token);
    }

    return data.token;
  } catch (error) {
    console.error("Error creating dev auth token:", error);
    return null;
  }
};

// Check if user is authenticated
export const isAuthenticated = async (): Promise<boolean> => {
  const token = await getAuthToken();
  return token !== null;
};

// Clear all auth data
export const clearAuth = async () => {
  // Clear cookies (web only)
  if (Platform.OS === "web" && typeof document !== "undefined") {
    document.cookie = "jwt=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  }

  // Clear shared storage
  await storage.removeItem("jwt_token");
  await storage.removeItem("cedar_user");
};
